// pages/MyPost.js
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import MediaGrid from "../components/MediaGrid";
import { IconMore } from "../components/FeedIcons";
import { formatPostTime } from "../utils/formatPostTime";
import { resizeImage } from "../utils/resizeImage";

import PostAvatar from "../components/PostAvatar";
import RepostCard from "../components/RepostCard";
import PostActionBar from "../components/PostActionBar";

import useDesktopComposer from "../hooks/useDesktopComposer";
import useMobileComposer from "../hooks/useMobileComposer";
import useBottomSheet from "../hooks/useBottomSheet";
import useZoomModal from "../hooks/useZoomModal";


/* 🔗 LINKIFY — makes links in text blue + clickable.
   Matches https://…, http://…, www.… and bare domains like coins.metisel.com */
const LINK_REGEX = /(?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|app|dev|xyz|info|me|ai|gh|uk|us|ng|edu|gov|tv|site|online|store|tech|biz|ly|to|cc)\b(?::\d{2,5})?(?:[/?#][^\s<]*)?/gi;

const splitLinks = (text) => {
  const out = [];
  let last = 0;
  let m;
  LINK_REGEX.lastIndex = 0;
  while ((m = LINK_REGEX.exec(text))) {
    const start = m.index;
    const isBare = !/^(?:https?:\/\/|www\.)/i.test(m[0]);
    // bare domain glued to an email / word (user@site.com) → not a link
    if (isBare && start > 0 && /[@\w.-]/.test(text[start - 1])) continue;
    // keep trailing punctuation out of the link
    const url = m[0].replace(/[.,;:!?)\]}'"]+$/, "");
    if (!url) continue;
    if (start > last) out.push({ text: text.slice(last, start) });
    out.push({ url });
    last = start + url.length;
    LINK_REGEX.lastIndex = last;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
};

const LinkifiedText = ({ text }) => {
  if (!text) return null;

  return splitLinks(String(text)).map((part, i) => {
    if (!part.url) return <React.Fragment key={i}>{part.text}</React.Fragment>;

    const href = /^https?:\/\//i.test(part.url) ? part.url : `https://${part.url}`;
    return (
      <a
        key={i}
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()} // don't trigger the parent's click
        className="text-blue-500 hover:underline break-all"
      >
        {part.url}
      </a>
    );
  });
};

// "Show more" cut must not land in the middle of a link
const sliceKeepingLinks = (text, limit) => {
  let end = limit;
  let pos = 0;
  for (const p of splitLinks(text)) {
    const len = (p.url ?? p.text).length;
    if (p.url && pos < end && pos + len > end) {
      end = pos + len;
      break;
    }
    pos += len;
  }
  return text.slice(0, end);
};

export default function MyPost({
  myChatId,
  users,
  myPosts,
  avatarUrl,
  onLikePost,
   myDisplayName,
  onRepost,
  onDeletePost,
  onEditPost,
  onBack,
  // cursor pagination
  onLoadMore,
  hasMore,
  loading,
    viewedUser,
    embedded = false, 
  onSupportPost,
  feedSocket,
  uswdWallet,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [zoomType, setZoomType] = useState("image");

  const [menu, setMenu] = useState({ open: false, post: null, type: null });
  const [expandedPosts, setExpandedPosts] = useState({});

  const [showComposer, setShowComposer] = useState(false);
  const [quotePost, setQuotePost] = useState(null);
  const [text, setText] = useState("");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreview, setMediaPreview] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isPosting, setIsPosting] = useState(false);
  const [isReplyMode, setIsReplyMode] = useState(false);
  const [replyToPost, setReplyToPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  // ── EARNINGS MODAL STATE (post author view) ─────────────────────────────
  const [earningsModal, setEarningsModal] = useState({ open: false, post: null });

  // ── SUPPORT MODAL STATE (viewer supporting the author's post) ───────────
  const [supportModal, setSupportModal] = useState({ open: false, post: null });
  const [supportAmount, setSupportAmount] = useState("");
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState("");
  const [supportResult, setSupportResult] = useState(null);

  const NETWORK_FEE_USWD = 0.01;

  const scrollContainerRef = useRef(null);

  // ── SCROLL-POSITION PAGINATION — mirrors Post.js exactly ─────────────────
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!hasMore || loading) return;

      const nearBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 500;

      if (!nearBottom) return;

      onLoadMore?.();
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading, onLoadMore]);
  // ─────────────────────────────────────────────────────────────────────────

  const toggleExpanded = (postId, e) => {
    e.stopPropagation();
    setExpandedPosts((prev) => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleMediaSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const remainingSlots = 4 - mediaFiles.length;
    if (remainingSlots <= 0) return;
    const limitedFiles = files.slice(0, remainingSlots);
    const processedFiles = [];
    const previews = [];
    for (const file of limitedFiles) {
      if (file.type.startsWith("image/")) {
        const resized = await resizeImage(file, { maxSize: 1280, quality: 0.8, mimeType: "image/jpeg" });
        processedFiles.push(resized);
        previews.push({ url: URL.createObjectURL(resized), type: "image" });
      } else {
        processedFiles.push(file);
        previews.push({ url: URL.createObjectURL(file), type: "video" });
      }
    }
    setMediaFiles((prev) => [...prev, ...processedFiles]);
    setMediaPreview((prev) => [...prev, ...previews]);
  };

  const handlePost = async () => {
    if (isPosting) return;
    if (!text.trim() && mediaFiles.length === 0 && !quotePost && !editingPost) return;
    setIsPosting(true);
    try {
      if (editingPost) {
        onEditPost({ postId: editingPost.postId, text: text?.trim() || null });
        setEditingPost(null);
        setText("");
        setMediaFiles([]);
        setMediaPreview([]);
        setShowComposer(false);
        return;
      }
      if (quotePost) {
        onRepost({ postId: quotePost.postId, chatId: myChatId, text: text?.trim() || null, originalPost: quotePost, authorDisplayName: myDisplayName, });
      }
      setText("");
      setMediaFiles([]);
      setMediaPreview([]);
      setQuotePost(null);
      setShowComposer(false);
    } catch (err) {
  
    } finally {
      setIsPosting(false);
    }
  };

  // ── EARNINGS MODAL HANDLERS (post author view) ───────────────────────────

  const handleOpenEarningsModal = ({ post }) => {
    setEarningsModal({ open: true, post });
  };

  const handleCloseEarningsModal = () => {
    setEarningsModal({ open: false, post: null });
  };

  // ── SUPPORT MODAL HANDLERS ────────────────────────────────────────────────

  const handleOpenSupportModal = ({ post }) => {
    setSupportModal({ open: true, post });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null);
  };

  const handleCloseSupportModal = () => {
    if (supportLoading) return;
    setSupportModal({ open: false, post: null });
    setSupportAmount("");
    setSupportError("");
    setSupportResult(null);
  };

  // ── SUPPORT RESULT LISTENER ────────────────────────────────────────────────
 
// ── SUPPORT RESULT LISTENER ────────────────────────────────────────────────
useEffect(() => {
  if (!feedSocket?.onSupportPostResult) return;
  const unsubscribe = feedSocket.onSupportPostResult((result) => {
    setSupportLoading(false);
    if (!result?.success) {
      toast?.error?.(result?.error || "Failed to send support");
      setSupportError(result?.error || "Failed to send support");
      return;
    }
    setSupportResult(result.data); // { amount, fee, total, ... }
  });
  return unsubscribe;
}, [feedSocket]);
   const handleSupportSubmit = async () => {
    const amount = parseFloat(supportAmount);

    if (!amount || amount <= 0) {
      setSupportError("Enter a valid amount");
      return;
    }

    const post = supportModal.post;
    if (!post?.postId) {
      setSupportError("Post not available");
      return;
    }

    setSupportLoading(true);
    setSupportError("");
    setSupportResult(null);

    try {
      const timestamp = Date.now();

      onSupportPost?.({
        postId: post.postId,
        amount,
        timestamp,
      });
      // NOTE: modal stays open (loading) until onSupportPostResult fires
    } catch (err) {
      setSupportError(err?.message || "Failed to send support");
      setSupportLoading(false);
    }
  };

     return (
    <div
      ref={scrollContainerRef}
      className={[
        "relative flex flex-col w-full",
        embedded
          ? ""
          : "h-full overflow-y-auto border-l border-neutral-200 dark:border-neutral-700",
      ].join(" ")}
      style={embedded ? undefined : { WebkitOverflowScrolling: "touch" }}
    >
      {/* ── HEADER ──
          Skipped when embedded: the host surface (e.g. UserInfoOverlay)
          already renders its own back button + title above this component. */}
      {!embedded && (
      <div
        className={[
          "sticky top-0 z-30",
          "bg-white/95 dark:bg-neutral-800/95 backdrop-blur-sm",
          "border-b border-neutral-200 dark:border-neutral-700",
          "pt-safe-top",
        ].join(" ")}
      >
               <div className="flex items-center gap-3 px-4 pt-3 pb-2">
          <button
  onClick={onBack}
  className="flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:opacity-60 touch-manipulation transition-colors"
  aria-label="Back"
>
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
</button>
                  <div className="text-xl font-bold text-green-600">
            {viewedUser
              ? `${viewedUser.displayName || viewedUser.username || "User"}'s Posts`
              : "My Posts"}
          </div>
          <div className="ml-auto md:hidden">
            {avatarUrl ? (
              <img src={avatarUrl} alt="Me" className="w-8 h-8 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-600" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-neutral-300 dark:bg-neutral-700 flex items-center justify-center text-sm">👤</div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* SCROLL AREA */}
      <div className="flex-1">
            {!myPosts?.length && !loading && (
  <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">
    {viewedUser
      ? `${viewedUser.displayName || viewedUser.username || "This user"} hasn't posted yet.`
      : "You haven't posted yet."}
  </div>
)}

        {myPosts?.map((post) => {
          const isPureRepost = post?.isRepost && !post?.isQuote && post?.originalPost;

          return (
            <div
              key={post.postId}
              className={[
                "px-3 sm:px-4 py-3 border-b border-neutral-200 dark:border-neutral-700",
                post.isDeleted
                  ? "cursor-default"
                  : "cursor-pointer active:bg-neutral-50 dark:active:bg-neutral-700/40 transition-colors duration-100",
              ].join(" ")}
              onClick={() =>
                navigate(location.pathname, {
                  state: {
                    view: "post",
                    post: isPureRepost ? post.originalPost : post,
                    fromScrollY: window.scrollY,
                  },
                })
              }
            >
              <div className="flex gap-3">
                <div className="shrink-0">
                  <PostAvatar post={post} users={users} myChatId={myChatId} avatarUrl={avatarUrl} />
                </div>

                <div className="flex-1 min-w-0">
                 {/* META ROW */}
<div className="flex items-start justify-between gap-2">
  <div className="flex items-center gap-1.5 flex-wrap text-sm min-w-0">
    <span className="font-semibold text-neutral-800 dark:text-neutral-200 truncate max-w-[120px] sm:max-w-none">
      {post.author?.displayName || post.author?.username || "Unknown"}
    </span>
    <span className="text-neutral-400 inline truncate max-w-[100px]">
      @{post.author?.username || "unknown"}
    </span>
    <span className="text-neutral-400 inline">·</span>
    <span className="text-neutral-400 text-xs whitespace-nowrap">
      {formatPostTime(post.createdAt)}
    </span>
    {post.isEdited && !post.isDeleted && (
      <span className="text-neutral-400 text-xs">(edited)</span>
    )}
  </div>

                                       {!post.isDeleted && String(post.author?.chatId) === String(myChatId) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenu({ open: true, post, type: "post-actions" });
                        }}
                        className="shrink-0 flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-95 transition-all touch-manipulation -mr-1"
                        style={{ minWidth: 32, minHeight: 32 }}
                        aria-label="More options"
                      >
                        <IconMore />
                      </button>
                    )}
                  </div>

                  {post.isDeleted ? (
                    <div className="mt-1 text-sm text-neutral-400 italic">This post was deleted.</div>
                  ) : (
                    <>
                      {(!post.isRepost || post.isQuote) && post.text && (() => {
                        const LIMIT = 100;
                        const isExpanded = expandedPosts[post.postId];
                        const isLong = post.text.length > LIMIT;
                        const displayText = isExpanded || !isLong ? post.text : sliceKeepingLinks(post.text, LIMIT) + "...";
                        return (
                          <div className="mt-1">
                            <div className="text-[15px] whitespace-pre-wrap break-words leading-relaxed text-neutral-800 dark:text-neutral-100">
                              <LinkifiedText text={displayText} />
                            </div>
                            {isLong && (
                              <button onClick={(e) => toggleExpanded(post.postId, e)} className="mt-1 text-blue-500 font-medium text-sm touch-manipulation">
                                {isExpanded ? "Show less" : "Show more"}
                              </button>
                            )}
                          </div>
                        );
                      })()}

                      {(!post.isRepost || post.isQuote) && post.media?.length > 0 && (
                        <div className="mt-2">
                          <MediaGrid
                            media={post.media}
                            onClick={(m) => { setZoomSrc(m.url); setZoomType(m.type); setZoomOpen(true); }}
                          />
                        </div>
                      )}

                      <RepostCard
                        post={post}
                        navigate={navigate}
                        location={location}
                        setZoomSrc={setZoomSrc}
                        setZoomType={setZoomType}
                        setZoomOpen={setZoomOpen}
                      />

                      <PostActionBar
                        post={post}
                        navigate={navigate}
                        location={location}
                        setRepostMenu={(payload) => setMenu({ ...payload, type: "repost" })}
                        onLikePost={onLikePost}
                        myChatId={myChatId}
                        setMenu={setMenu}
                        onSupportPost={onSupportPost ? handleOpenSupportModal : undefined}
                        onViewEarnings={handleOpenEarningsModal}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        <div
          style={{ height: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}
          aria-hidden="true"
        />
      </div>

      {useZoomModal({ zoomOpen, setZoomOpen, zoomSrc, zoomType })}

      {useBottomSheet({
        menu, setMenu, onRepost, myChatId,
        setQuotePost, setShowComposer, setIsReplyMode, setReplyToPost,
        onDeletePost,
        onEditPost: (post) => {
          setEditingPost(post);
          setText(post.text || "");
          setShowComposer(true);
        },
      })}

      {useMobileComposer({
        showComposer, setShowComposer, avatarUrl, text, setText,
        mediaFiles, mediaPreview, uploadProgress, isPosting,
        handleMediaSelect, handlePost, quotePost, setQuotePost,
        setMediaFiles, setMediaPreview, setIsReplyMode, setReplyToPost,
      })}

      {useDesktopComposer({
        showComposer, setShowComposer, text, setText,
        mediaFiles, mediaPreview, isPosting, handleMediaSelect,
        handlePost, handleSendComment: () => {}, isReplyMode, replyToPost,
        quotePost, setQuotePost, setMediaFiles, setMediaPreview,
        setIsReplyMode, setReplyToPost,
      })}

           {/* ── EARNINGS MODAL (post author — read-only) ────────────────────────── */}
      {earningsModal.open && earningsModal.post && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={handleCloseEarningsModal}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                Post earnings
              </span>
              <button
                onClick={handleCloseEarningsModal}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="px-5 pb-6">
              {/* Post preview */}
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl border border-neutral-200 dark:border-neutral-600 px-3 py-2.5 mb-4 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                {earningsModal.post.text ? (
                  <span>{earningsModal.post.text}</span>
                ) : (
                  <span className="italic text-neutral-400">(media post)</span>
                )}
              </div>

              {/* Received for this post */}
              <div>
                <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
                  Received for this post
                </div>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50">
                  <span className="text-lg font-semibold text-neutral-800 dark:text-neutral-100">
                    {(earningsModal.post.supportsAmount ?? earningsModal.post.supportReceived ?? 0).toFixed(2)}
                  </span>
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">USWD</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

          {/* ── SUPPORT POST MODAL (viewer supporting the author) ─────────────── */}
      {supportModal.open && supportModal.post && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/40"
          style={{ backdropFilter: "blur(2px)" }}
          onClick={handleCloseSupportModal}
        >
          <div
            className="bg-white dark:bg-neutral-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <span className="font-semibold text-base text-neutral-900 dark:text-neutral-100">
                Support this post
              </span>
              <button
                onClick={handleCloseSupportModal}
                disabled={supportLoading}
                className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-40"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-5 pb-6">
              {supportResult ? (
                /* ── SUCCESS / RECEIPT VIEW ───────────────────────────── */
                <div className="text-center">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-300 mb-4">
                    Your support was sent successfully.
                  </div>
                  <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50 divide-y divide-neutral-200 dark:divide-neutral-600 mb-4 text-sm overflow-hidden">
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {Number(supportResult.amount).toFixed(2)} USWD
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                      <span className="font-medium text-neutral-800 dark:text-neutral-100">
                        {Number(supportResult.fee).toFixed(2)} USWD
                      </span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-neutral-700 dark:text-neutral-200 font-semibold">Total</span>
                      <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                        {Number(supportResult.total).toFixed(2)} USWD
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseSupportModal}
                    className="w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 text-white font-semibold text-sm transition-colors touch-manipulation"
                  >
                    Done
                  </button>
                </div>
              ) : (
                /* ── INPUT FORM ───────────────────────────────────────── */
                <>
                  {/* Post preview */}
                  <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl border border-neutral-200 dark:border-neutral-600 px-3 py-2.5 mb-4 text-sm text-neutral-600 dark:text-neutral-300 line-clamp-2">
                    <span className="font-medium text-neutral-800 dark:text-neutral-100">
                      @{supportModal.post.author?.username}
                    </span>
                    {supportModal.post.text ? (
                      <span> {supportModal.post.text}</span>
                    ) : (
                      <span className="italic text-neutral-400"> (media post)</span>
                    )}
                  </div>

                  {/* Amount label */}
                  <label
                    htmlFor="my-post-support-amount-input"
                    className="block text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1.5"
                  >
                    Amount (USWD)
                  </label>

                  {/* Amount row */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-lg px-3 py-2 select-none whitespace-nowrap">
                      USWD
                    </span>
                    <input
                      id="my-post-support-amount-input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      value={supportAmount}
                      onChange={(e) => {
                        setSupportAmount(e.target.value);
                        setSupportError("");
                      }}
                      disabled={supportLoading}
                      className="flex-1 bg-transparent border border-neutral-200 dark:border-neutral-600 focus:border-blue-400 dark:focus:border-blue-500 rounded-xl px-3 py-2 text-base outline-none focus:ring-2 focus:ring-blue-400/30 dark:focus:ring-blue-500/30 transition-colors disabled:opacity-50"
                      autoFocus
                    />
                  </div>

                  {/* Quick-select presets */}
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {[1, 5, 10, 25, 50].map((v) => (
                      <button
                        key={v}
                        onClick={() => { setSupportAmount(String(v)); setSupportError(""); }}
                        disabled={supportLoading}
                        className="text-xs px-3 py-1 rounded-full border border-neutral-200 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-40 touch-manipulation"
                      >
                        {v}
                      </button>
                    ))}
                  </div>

                  {/* Amount / fee / total preview */}
                  {parseFloat(supportAmount) > 0 && (
                    <div className="rounded-xl border border-neutral-200 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-700/50 divide-y divide-neutral-200 dark:divide-neutral-600 mb-3 text-sm overflow-hidden">
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Amount</span>
                        <span className="font-medium text-neutral-800 dark:text-neutral-100">
                          {parseFloat(supportAmount).toFixed(2)} USWD
                        </span>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-500 dark:text-neutral-400">Network fee</span>
                        <span className="font-medium text-neutral-800 dark:text-neutral-100">
                          {NETWORK_FEE_USWD.toFixed(2)} USWD
                        </span>
                      </div>
                      <div className="flex justify-between px-3 py-2">
                        <span className="text-neutral-700 dark:text-neutral-200 font-semibold">Total</span>
                        <span className="font-semibold text-neutral-900 dark:text-neutral-50">
                          {(parseFloat(supportAmount) + NETWORK_FEE_USWD).toFixed(2)} USWD
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Error message */}
                  {supportError && (
                    <p className="text-xs text-red-500 dark:text-red-400 mb-3 -mt-1">
                      {supportError}
                    </p>
                  )}

                  {/* Submit button */}
                  <button
                    onClick={handleSupportSubmit}
                    disabled={supportLoading || !supportAmount || parseFloat(supportAmount) <= 0}
                    className="w-full py-2.5 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors touch-manipulation"
                  >
                    {supportLoading ? "Sending…" : "Send support"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}